import { FC, useEffect, useState } from 'react';
import { useLazyGetJobAdjustmentsQuery } from '../../api';
import SidePanel from 'components/side-panel/SidePanel';
import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import { NavCloseIcon } from 'assets/icons';
import { Loader } from 'components';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import ImagePreview from 'components/image-preview/ImagePreview';
import { AttachmentFile } from 'types/baseTypes';
import { useParams } from 'react-router-dom';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../../events';

interface ExplainAdjustmentsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
}

const ExplainAdjustmentsPanel: FC<ExplainAdjustmentsPanelProps> = ({ isOpen, onClose, jobId }) => {
  const { id: teamId } = useAppSelector(selectCurrentTeam);
  const { payRunId, merchantId } = useParams();
  const [getJobAdjustments, { data: adjustmentsData, isLoading }] = useLazyGetJobAdjustmentsQuery();
  const [selectedReceipt, setSelectedReceipt] = useState<AttachmentFile | null>(null);

  useEffect(() => {
    if (isOpen && teamId && jobId) {
      getJobAdjustments({ teamId, jobId, payRunId, merchantId });
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunExplainJobAddAdjustmentView);
    }
  }, [isOpen, teamId, jobId, getJobAdjustments]);

  const adjustments = adjustmentsData?.adjustments || [];
  const totalAmount = adjustmentsData?.totalAmount || '0';

  const handleViewReceipt = (receiptUrl: string, title: string) => {
    setSelectedReceipt({
      url: receiptUrl,
      urlLink: receiptUrl,
      title: title
    });
  };

  const handleCloseImagePreview = () => {
    setSelectedReceipt(null);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader />
        </div>
      );
    }

    if (adjustments.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <p className="text-lg font-semibold text-primaryText">No adjustments found</p>
          <p className="text-sm text-accentText">There are no adjustments for this job.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {adjustments.map(adjustment => (
          <div key={adjustment.id} className="space-y-2 border-b border-secondaryBtn pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sbase font-semibold text-primaryText">{adjustment.title}</p>
                <p className="text-px13 font-semibold text-accentText">
                  {DateUtils.getDateInFormat({
                    date: new Date(adjustment.postedDate),
                    dateFormat: 'MMM dd, yyyy'
                  })}
                </p>
              </div>
              <div>
                <p className="text-right text-sbase font-semibold text-primaryText">
                  {getAmountWithCurrency(adjustment.amount)}
                </p>
                {adjustment.receiptUrl && (
                  <button
                    className="text-px13 text-secondary hover:underline focus:outline-none"
                    onClick={() => handleViewReceipt(adjustment.receiptUrl, adjustment.title)}
                    aria-label={`View receipt for ${adjustment.title}`}>
                    View receipt
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <span className="text-17px font-bold text-heading">Total job adjustments</span>
          <span className="text-17px font-bold text-heading">{getAmountWithCurrency(totalAmount ?? 0)}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <SidePanel isOpen={isOpen} onClose={onClose}>
        <div className="flex h-full w-96 flex-col">
          {/* Header */}
          <div className="flex items-center gap-2 p-4">
            <button onClick={onClose} className="focus:outline-none" aria-label="Close panel">
              <NavCloseIcon className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold text-primaryText">Job adjustments</h2>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">{renderContent()}</div>
        </div>
      </SidePanel>

      {selectedReceipt && (
        <ImagePreview images={[selectedReceipt]} selectedIndex={0} onClose={handleCloseImagePreview} />
      )}
    </>
  );
};

export default ExplainAdjustmentsPanel;
